import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities/api-entitites';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import SliceRequest from '../slice-request';

export type CompanyDocumentType = {
  code: string;
  label: string;
  category_code: string;
  category_label: string;
  frequency: 'frequent' | 'conditional';
};

export type CompanyDocumentCategory = {
  code: string;
  label: string;
};

type CompanyDocumentTypesState = {
  status: 'idle' | 'pending';
  error: ApiError;
  items: CompanyDocumentType[];
};

const initialState: CompanyDocumentTypesState = {
  status: 'idle',
  error: ApiErrorInitialState,
  items: [],
};

export const fetchCompanyDocumentTypes = SliceRequest<
  CompanyDocumentType[],
  { api: ClientApiV2; locale: string }
>('fetchCompanyDocumentTypes', async ({ api, locale }) => {
  const response = await api.get<CompanyDocumentType[]>(
    '/company-document-types',
    { params: { locale } },
  );
  return response.data;
});

const companyDocumentTypesSlice = createSlice({
  name: 'companyDocumentTypes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCompanyDocumentTypes.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(
      fetchCompanyDocumentTypes.fulfilled,
      (state, { payload }) => {
        state.items = payload;
        state.status = 'idle';
      },
    );
    builder.addCase(
      fetchCompanyDocumentTypes.rejected,
      (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.status = 'idle';
      },
    );
  },
});

export const selectCompanyDocumentTypes = (state: RootState) =>
  state.companyDocumentTypes.items;

export const selectCompanyDocumentCategories = createSelector(
  [selectCompanyDocumentTypes],
  (documentTypes) =>
    Array.from(
      documentTypes
        .reduce<Map<string, CompanyDocumentCategory>>(
          (categories, documentType) =>
            categories.set(documentType.category_code, {
              code: documentType.category_code,
              label: documentType.category_label,
            }),
          new Map(),
        )
        .values(),
    ),
);

export const selectCompanyDocumentTypesByCategory = createSelector(
  [
    selectCompanyDocumentTypes,
    (_state: RootState, categoryCode: string | undefined) => categoryCode,
  ],
  (documentTypes, categoryCode) =>
    categoryCode
      ? documentTypes.filter(
          (documentType) => documentType.category_code === categoryCode,
        )
      : [],
);

export const companyDocumentTypesReducer = companyDocumentTypesSlice.reducer;
